import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { JackstoneLogoLink } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { LogOut, Plus, ClipboardList, Home, Sun, Moon, Briefcase, ChevronDown, RefreshCw } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

/**
 * Stripped-down shell for shop-floor employees.
 * - No sidebar — just a top bar with big touch-friendly buttons.
 * - Smaller surface area to reduce confusion for non-technical users.
 * - Inherits High Contrast / Large Text mode from <html class="hc-mode">.
 */
export default function EmployeeLayout() {
  const { user, logout, switchUser } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const onLogout = () => {
    logout();
    navigate("/login");
  };

  const onSwitchUser = () => {
    switchUser();
    navigate("/login");
  };

  // Show navigation tabs only on the home/list views, hide them on detail/form pages.
  const showNav = ["/me", "/tests", "/orders"].includes(location.pathname);

  const navItems = [
    { to: "/me", label: "Home", icon: Home, testid: "emp-nav-home" },
    { to: "/tests", label: "All tests", icon: ClipboardList, testid: "emp-nav-tests" },
    { to: "/tests/new", label: "New test", icon: Plus, testid: "emp-nav-new" },
    { to: "/orders", label: "Jobs", icon: Briefcase, testid: "emp-nav-orders" },
  ];
  const currentNav =
    navItems.find((n) => n.to === location.pathname) || navItems[0];

  return (
    <div className="min-h-screen bg-[#f5f1ec]">
      <header className="bg-[#33424f] text-white border-b-4 border-[#ccb795]">
        <div className="px-4 md:px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-6 min-w-0">
            <JackstoneLogoLink to="/me" variant="light" className="h-7 md:h-8 w-auto shrink-0" />
            <div className="hidden md:block subtitle text-[10px] uppercase tracking-[0.3em] text-[#ccb795]">
              Pressure Test Registry · Test Person
            </div>
          </div>
          <div className="flex items-center gap-2 md:gap-3 shrink-0">
            <div className="text-right hide-in-hc hidden sm:block">
              <div data-testid="employee-display-name" className="text-sm subtitle">{user?.name}</div>
              <div className="text-[10px] subtitle uppercase tracking-[0.2em] text-[#ccb795]">
                {user?.username}
              </div>
            </div>
            <Button
              data-testid="dark-mode-toggle"
              onClick={toggleDarkMode}
              variant="ghost"
              className="text-white hover:bg-[#1f2a33] h-10 w-10 p-0"
              title={darkMode ? "Switch to light mode" : "Switch to dark mode"}
            >
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <Button
              data-testid="switch-user-btn"
              onClick={onSwitchUser}
              variant="outline"
              className="border-[#ccb795] text-[#ccb795] hover:bg-[#1f2a33] hover:text-white h-10 subtitle uppercase tracking-[0.15em] text-xs px-2 md:px-3"
            >
              <RefreshCw className="w-4 h-4 md:mr-2" /> <span className="hidden md:inline">Switch user</span>
            </Button>
            <Button
              data-testid="logout-btn"
              onClick={onLogout}
              variant="outline"
              className="border-[#ccb795] text-[#ccb795] hover:bg-[#1f2a33] hover:text-white h-10 subtitle uppercase tracking-[0.15em] text-xs px-2 md:px-3"
            >
              <LogOut className="w-4 h-4 md:mr-2" /> <span className="hidden md:inline">Sign out</span>
            </Button>
          </div>
        </div>

        {showNav && (
          <>
            {/* Desktop horizontal tabs — unchanged */}
            <nav className="hidden md:flex px-6 pb-3 gap-2">
              {navItems.map(({ to, label, icon: Icon, testid }) => (
                <NavLink
                  key={to}
                  to={to}
                  data-testid={testid}
                  className={({ isActive }) =>
                    `subtitle uppercase tracking-[0.15em] text-xs flex items-center gap-2 px-4 py-2 border ${
                      isActive
                        ? "bg-[#ccb795] text-[#33424f] border-[#ccb795]"
                        : "border-[#1f2a33] text-white hover:bg-[#1f2a33]"
                    }`
                  }
                >
                  <Icon className="w-4 h-4" /> {label}
                </NavLink>
              ))}
            </nav>

            {/* Mobile dropdown nav */}
            <div className="md:hidden px-4 pb-3" data-testid="emp-mobile-nav-bar">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="emp-mobile-nav-trigger"
                    className="w-full flex items-center justify-between gap-2 bg-[#262f39] border border-[#1f2a33] px-3 py-2.5 text-xs subtitle uppercase tracking-[0.2em] text-white"
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      {currentNav?.icon && <currentNav.icon className="w-4 h-4 shrink-0 text-[#ccb795]" />}
                      <span className="truncate">{currentNav?.label || "Menu"}</span>
                    </span>
                    <ChevronDown className="w-4 h-4 shrink-0 opacity-70" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="w-[calc(100vw-2rem)]"
                  data-testid="emp-mobile-nav-content"
                >
                  <DropdownMenuLabel className="text-[10px] subtitle uppercase tracking-[0.25em] text-[#50493b]">
                    Test Person
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {navItems.map(({ to, label, icon: Icon, testid }) => (
                    <DropdownMenuItem
                      key={to}
                      data-testid={`mobile-${testid}`}
                      onClick={() => navigate(to)}
                      className="cursor-pointer text-sm subtitle"
                    >
                      <Icon className="w-4 h-4 mr-2 text-[#39678f]" />
                      {label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </>
        )}
      </header>

      <main>
        <Outlet />
      </main>
    </div>
  );
}
